import React, { useEffect, useState } from 'react';
import Select from 'react-select';
import { formatPhoneNumberIntl } from 'react-phone-number-input';
import { useFormik } from 'formik';
import PhoneInput from 'react-phone-input-2';
import * as Yup from 'yup';
import { Link, useNavigate } from 'react-router-dom';
import Cookies from 'js-cookie';
import { Reorder } from 'framer-motion';
import { Item } from 'features/profile/Item';
import TagManager from 'react-gtm-module';

import styles from './RequestMeeting.module.scss';
import { RESIDENT_COUNTRIES } from 'common/constants';
import { useGetAllCountriesQuery, useSubmitRequestMeetingFormMutation } from 'services/exploreApi';

interface FormData {
  First_Name: string;
  Last_Name: string;
  Email: string;
  Phone: string;
  PhoneConfirmation: string;
  Country_of_Residence: string;
  Preferred_Countries: string[];
  Preferred_Subjects: string[];
  Preferred_Program_Level: string;
  Preferred_Budget: string;
  Preferred_Intake_Year: string;
  Intake_Period: string;
  Academic_Perfomance: string;
  English_Level: string;
  Priorities: string[];
  Institution_Interest: string;
  Comments: string;
  Term_Policy: boolean;
}

const residentOptions = RESIDENT_COUNTRIES.map((v: string, i: number) => ({ label: v, value: v }));
const degreeTypeOptions = [
  { label: 'Bachelor', value: 'Bachelor' },
  { label: 'Master', value: 'Master' },
  { label: 'PhD', value: 'PhD' },
  { label: 'MBA', value: 'MBA' },
  { label: 'Postgraduate Diploma', value: 'Postgraduate Diploma' },
];

const levelOptions = [
  { label: 'Poor', value: 'Poor' },
  { label: 'Average', value: 'Average' },
  { label: 'Good', value: 'Good' },
  { label: 'Excelent', value: 'Excelent' },
];

const prioritiesOptions = ['Academic Reputation', 'Career oportunities', 'Budget', 'Location'];
const preferredBudgetOptions = [
  { label: 'Less than 5k', value: 'Less than 5k' },
  { label: '5k - 10k', value: '5k - 10k' },
  { label: '10k - 20k', value: '10k - 20k' },
  { label: '20k - 30k', value: '20k - 30k' },
  { label: '30k - 50k', value: '30k - 50k' },
  { label: 'More than 50k', value: 'More than 50k' },
  { label: 'Any', value: 'Any' },
];

const preferredIntakeYearOptions = [
  { label: '2024', value: '2024' },
  { label: '2025', value: '2025' },
  { label: '2026', value: '2026' },
];

const intakePeriodOptions = [
  { label: 'January - April', value: 'January - April' },
  { label: 'May - August', value: 'May - August' },
  { label: 'September - December', value: 'September - December' },
  { label: 'Any', value: 'Any' },
];
const subjects = [
  { label: 'Business', value: 'Business' },
  { label: 'Finance', value: 'Finance' },
  { label: 'Health', value: 'Health' },
  { label: 'Law', value: 'Law' },
  { label: 'Arts & Music', value: 'Arts & Music' },
  { label: 'Literature', value: 'Literature' },
  { label: 'Social Studies and history', value: 'Social Studies and history' },
  { label: 'Engineering', value: 'Engineering' },
  { label: 'Agriculture', value: 'Agriculture' },
  { label: 'Energy', value: 'Energy' },
  { label: 'Education', value: 'Education' },
  { label: 'Biological and biomedical sciences', value: 'Biological and biomedical sciences' },
  { label: 'Mathematics & Statistics', value: 'Mathematics & Statistics' },
  { label: 'Economics', value: 'Economics' },
  { label: 'Computer & Information Sciences', value: 'Computer & Information Sciences' },
  { label: 'Visual and performing arts', value: 'Visual and performing arts' },
  { label: 'Psychology', value: 'Psychology' },
  { label: 'Media, Communication and journalism', value: 'Media, Communication and journalism' },
  { label: 'International Relations', value: 'International Relations' },
  { label: 'Political Science', value: 'Political Science' },
  { label: 'Accounting', value: 'Accounting' },
  { label: 'Physics', value: 'Physics' },
  { label: 'Design', value: 'Design' },
  { label: 'Sports Science & Management', value: 'Sports Science & Management' },
  { label: 'Architecture', value: 'Architecture' },
];

const FormSchema = Yup.object().shape({
  First_Name: Yup.string().min(2, 'Too Short!').max(70, 'Too Long!').required('Required'),
  Last_Name: Yup.string().min(2, 'Too Short!').required('Required'),
  Email: Yup.string().email('Invalid email').required('Required'),
  Phone: Yup.string().min(10, 'Too Short!').required('Required'),
  PhoneConfirmation: Yup.string()
    .min(10, 'Too Short!')
    .required('Required')
    .oneOf([Yup.ref('Phone')], 'Your values does not match'),
  Country_of_Residence: Yup.string().required('Required'),
  Preferred_Countries: Yup.array()
    .of(Yup.string())
    .min(1, 'At least one country must be selected')
    .required('Preferred countries are required'),
  Preferred_Subjects: Yup.array()
    .of(Yup.string())
    .min(1, 'At least one subject must be selected')
    .required('Preferred subjects are required'),
  Preferred_Program_Level: Yup.string().required('Required'),
  Preferred_Budget: Yup.string().required('Required'),
  Preferred_Intake_Year: Yup.string().required('Required'),
  Intake_Period: Yup.string().required('Required'),
  Academic_Perfomance: Yup.string().required('Required'),
  English_Level: Yup.string().required('Required'),
  Term_Policy: Yup.boolean()
    .oneOf([true], 'You must accept the terms and policy')
    .required('You must accept the terms and policy'),
});
const RequestMeeting = (): React.ReactElement => {
  const { data: countries } = useGetAllCountriesQuery();
  const [submitForm, { isLoading }] = useSubmitRequestMeetingFormMutation();
  const navigate = useNavigate();
  const [priorities, setPriorities] = useState(prioritiesOptions);

  const sendGTMEvent = () => {
    TagManager.dataLayer({
      dataLayer: {
        event: 'qualified_lead',
      },
    });
  };

  const formik = useFormik<FormData>({
    initialValues: {
      First_Name: '',
      Last_Name: '',
      Email: '',
      Phone: '',
      Country_of_Residence: '',
      PhoneConfirmation: '',
      Preferred_Countries: [],
      Preferred_Subjects: [],
      Preferred_Program_Level: '',
      Preferred_Budget: '',
      Preferred_Intake_Year: '',
      Intake_Period: '',
      Academic_Perfomance: '',
      English_Level: '',
      Priorities: [],
      Institution_Interest: '',
      Comments: '',
      Term_Policy: false,
    },
    validationSchema: FormSchema,
    onSubmit: async (formData: FormData) => {
      if (
        ['Any', 'Less than 5k', '5k - 10k'].indexOf(formData.Preferred_Budget) == -1 &&
        formData.Preferred_Intake_Year == '2024'
      ) {
        sendGTMEvent();
      }
      const data = {
        ...formData,
        phone: formatPhoneNumberIntl('+' + formik.values.Phone),
        utm_medium: Cookies.get('__lt_utm_medium'),
        utm_source: Cookies.get('__lt_utm_source'),
        utm_term: Cookies.get('__lt_utm_term'),
        utm_campaign: Cookies.get('__lt_utm_campaign'),
        utm_content: Cookies.get('__lt_utm_content'),
      };

      submitForm({
        data: data,
      }).then(() => {
        navigate('/request-meeting-confirmation');
      });
    },
  });
  useEffect(() => {
    formik.setFieldValue('Priorities', priorities);
  }, [priorities]);

  return (
    <div className="container pt-5 mb-5 pb-5">
      <div className="row">
        <div className="col-12 col-md-6 order-md-1 order-2">
          <h2 className="mt-4 mb-3"> Request a Meeting</h2>
          <p>
            {' '}
            Please fill the following information for us to understand more about your profile and
            we will get back to you.
          </p>
          <form onSubmit={formik.handleSubmit} className="mt-5">
            <div>
              <label htmlFor="First_Name" className={`form-label ${styles.formLabel}`}>
                First Name *
              </label>
              <input
                name="First_Name"
                type="text"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.First_Name}
                className={`form-control ${styles.formInput}`}
              />
              {formik.touched.First_Name && formik.errors.First_Name ? (
                <div className="text-danger">{formik.errors.First_Name}</div>
              ) : null}
            </div>
            <div>
              <label htmlFor="Last_Name" className={`form-label ${styles.formLabel}`}>
                Last Name *
              </label>
              <input
                name="Last_Name"
                type="text"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.Last_Name}
                className={`form-control ${styles.formInput}`}
              />
              {formik.touched.Last_Name && formik.errors.Last_Name ? (
                <div className="text-danger">{formik.errors.Last_Name}</div>
              ) : null}
            </div>
            <div>
              <label htmlFor="Email" className={`form-label ${styles.formLabel}`}>
                Contact Email *
              </label>
              <input
                name="Email"
                type="text"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.Email}
                className={`form-control ${styles.formInput}`}
              />
              {formik.touched.Email && formik.errors.Email ? (
                <div className="text-danger">{formik.errors.Email}</div>
              ) : null}
            </div>
            <div>
              <label htmlFor="Phone" className={`form-label ${styles.formLabel}`}>
                Whatsapp Phone *
              </label>
              <PhoneInput
                inputClass={`form-control ${styles.formInput}`}
                country={'gb'}
                countryCodeEditable={false}
                onChange={(e) => {
                  formik.setFieldValue('Phone', e);
                }}
                onBlur={formik.handleBlur}
                inputProps={{
                  name: 'Phone',
                }}
              />
              {formik.touched.Phone && formik.errors.Phone ? (
                <div className="text-danger">{formik.errors.Phone}</div>
              ) : null}
            </div>
            <div>
              <label htmlFor="Phone" className={`form-label ${styles.formLabel}`}>
                Whatsapp Phone Confirmation *
              </label>
              <PhoneInput
                inputClass={`form-control ${styles.formInput}`}
                country={'gb'}
                countryCodeEditable={false}
                onChange={(e) => {
                  formik.setFieldValue('PhoneConfirmation', e);
                }}
                onBlur={formik.handleBlur}
                inputProps={{
                  name: 'PhoneConfirmation',
                }}
              />
              {formik.touched.PhoneConfirmation && formik.errors.PhoneConfirmation ? (
                <div className="text-danger">{formik.errors.PhoneConfirmation}</div>
              ) : null}
            </div>
            <div>
              <label htmlFor="Country_of_Residence" className={`form-label ${styles.formLabel}`}>
                Country of Residence *
              </label>
              <Select
                isSearchable
                classNamePrefix="react-select"
                className={`${styles.formSelect} p-0`}
                name="Country_of_Residence"
                options={residentOptions}
                onChange={(e) => formik.setFieldValue('Country_of_Residence', e?.label)}
                onBlur={() => formik.setFieldTouched('Country_of_Residence', true)}
                styles={{
                  control: (baseStyles: any) => ({
                    ...baseStyles,
                    height: '100%',
                    width: '100%',
                    borderRadius: '8px',
                    border: 'none',
                  }),
                }}
              />
              {formik.touched.Country_of_Residence && formik.errors.Country_of_Residence ? (
                <div className="text-danger">{formik.errors.Country_of_Residence}</div>
              ) : null}
            </div>
            <div>
              <label htmlFor="Preferred_Countries" className={`form-label ${styles.formLabel}`}>
                {'Where do you want to study? * (You can choose more than one)'}
              </label>
              <Select
                isSearchable
                isMulti
                name="Preferred_Countries"
                classNamePrefix="react-select"
                className={`${styles.formSelect} p-0`}
                options={countries?.data.map((c) => ({ label: c.name, value: c.name })) || []}
                isOptionDisabled={() => formik.values.Preferred_Countries.length >= 5}
                onChange={(selectedOptions) => {
                  const limitedSelection = selectedOptions.slice(0, 5);
                  const selectedLabels = limitedSelection.map((option) => option.label);
                  formik.setFieldValue('Preferred_Countries', selectedLabels);
                }}
                onBlur={() => formik.setFieldTouched('Preferred_Countries', true)}
                styles={{
                  control: (baseStyles: any) => ({
                    ...baseStyles,
                    height: '100%',
                    width: '100%',
                    borderRadius: '8px',
                    border: 'none',
                  }),
                }}
              />
              {formik.touched.Preferred_Countries && formik.errors.Preferred_Countries ? (
                <div className="text-danger">{formik.errors.Preferred_Countries}</div>
              ) : null}
            </div>
            <div>
              <label htmlFor="Preferred_Subjects" className={`form-label ${styles.formLabel}`}>
                {'What you do want to study? * (You can choose more than 1)?'}
              </label>
              <Select
                isSearchable
                isMulti
                name="Preferred_Subjects"
                classNamePrefix="react-select"
                className={`${styles.formSelect} p-0`}
                options={subjects}
                onChange={(selectedOptions) => {
                  const selectedLabels = selectedOptions.map((option) => option.label);
                  formik.setFieldValue('Preferred_Subjects', selectedLabels);
                }}
                onBlur={() => formik.setFieldTouched('Preferred_Subjects', true)}
                isOptionDisabled={() => formik.values.Preferred_Subjects.length >= 5}
                styles={{
                  control: (baseStyles: any) => ({
                    ...baseStyles,
                    height: '100%',
                    width: '100%',
                    borderRadius: '8px',
                    border: 'none',
                  }),
                }}
              />
              {formik.touched.Preferred_Subjects && formik.errors.Preferred_Subjects ? (
                <div className="text-danger">{formik.errors.Preferred_Subjects}</div>
              ) : null}
            </div>
            <div>
              <label htmlFor="Preferred_Program_Level" className={`form-label ${styles.formLabel}`}>
                Which degree type do you want to study? *
              </label>
              <Select
                isSearchable
                classNamePrefix="react-select"
                className={`${styles.formSelect} p-0`}
                options={degreeTypeOptions}
                name="Preferred_Program_Level"
                onChange={(e) => formik.setFieldValue('Preferred_Program_Level', e?.label)}
                onBlur={() => formik.setFieldTouched('Preferred_Program_Level', true)}
                styles={{
                  control: (baseStyles: any) => ({
                    ...baseStyles,
                    height: '100%',
                    width: '100%',
                    borderRadius: '8px',
                    border: 'none',
                  }),
                }}
              />
              {formik.touched.Preferred_Program_Level && formik.errors.Preferred_Program_Level ? (
                <div className="text-danger">{formik.errors.Preferred_Program_Level}</div>
              ) : null}
            </div>
            <div>
              <label htmlFor="Preferred_Budget" className={`form-label ${styles.formLabel}`}>
                What is your budget for the tuition fee? USD yearly *
              </label>
              <Select
                isSearchable
                classNamePrefix="react-select"
                className={`${styles.formSelect} p-0`}
                options={preferredBudgetOptions}
                onChange={(e) => formik.setFieldValue('Preferred_Budget', e?.label)}
                onBlur={() => formik.setFieldTouched('Preferred_Budget', true)}
                styles={{
                  control: (baseStyles: any) => ({
                    ...baseStyles,
                    height: '100%',
                    width: '100%',
                    borderRadius: '8px',
                    border: 'none',
                  }),
                }}
              />
              {formik.touched.Preferred_Budget && formik.errors.Preferred_Budget ? (
                <div className="text-danger">{formik.errors.Preferred_Budget}</div>
              ) : null}
            </div>
            <div>
              <label htmlFor="Preferred_Intake_Year" className={`form-label ${styles.formLabel}`}>
                Which year do you want to start your studies? *
              </label>
              <Select
                isSearchable
                classNamePrefix="react-select"
                className={`${styles.formSelect} p-0`}
                name="Preferred_Intake_Year"
                options={preferredIntakeYearOptions}
                onChange={(e) => formik.setFieldValue('Preferred_Intake_Year', e?.label)}
                onBlur={() => formik.setFieldTouched('Preferred_Intake_Year', true)}
                styles={{
                  control: (baseStyles: any) => ({
                    ...baseStyles,
                    height: '100%',
                    width: '100%',
                    borderRadius: '8px',
                    border: 'none',
                  }),
                }}
              />
              {formik.touched.Preferred_Intake_Year && formik.errors.Preferred_Intake_Year ? (
                <div className="text-danger">{formik.errors.Preferred_Intake_Year}</div>
              ) : null}
            </div>
            <div>
              <label htmlFor="Intake_Period" className={`form-label ${styles.formLabel}`}>
                In which period of the year do you want to start your studies? *
              </label>
              <Select
                isSearchable
                classNamePrefix="react-select"
                className={`${styles.formSelect} p-0`}
                name="Intake_Period"
                options={intakePeriodOptions}
                onChange={(e) => formik.setFieldValue('Intake_Period', e?.label)}
                onBlur={() => formik.setFieldTouched('Intake_Period', true)}
                styles={{
                  control: (baseStyles: any) => ({
                    ...baseStyles,
                    height: '100%',
                    width: '100%',
                    borderRadius: '8px',
                    border: 'none',
                  }),
                }}
              />
              {formik.touched.Intake_Period && formik.errors.Intake_Period ? (
                <div className="text-danger">{formik.errors.Intake_Period}</div>
              ) : null}
            </div>
            <div>
              <label htmlFor="Academic_Perfomance" className={`form-label ${styles.formLabel}`}>
                How was your academic performance in high school/university? *
              </label>
              <Select
                isSearchable
                classNamePrefix="react-select"
                className={`${styles.formSelect} p-0`}
                name="Academic_Perfomance"
                options={levelOptions}
                onChange={(e) => formik.setFieldValue('Academic_Perfomance', e?.label)}
                onBlur={() => formik.setFieldTouched('Academic_Perfomance', true)}
                styles={{
                  control: (baseStyles: any) => ({
                    ...baseStyles,
                    height: '100%',
                    width: '100%',
                    borderRadius: '8px',
                    border: 'none',
                  }),
                }}
              />
              {formik.touched.Academic_Perfomance && formik.errors.Academic_Perfomance ? (
                <div className="text-danger">{formik.errors.Academic_Perfomance}</div>
              ) : null}
            </div>
            <div>
              <label htmlFor="English_Level" className={`form-label ${styles.formLabel}`}>
                How do you rate your English level? *
              </label>
              <Select
                isSearchable
                classNamePrefix="react-select"
                className={`${styles.formSelect} p-0`}
                name="English_Level"
                options={levelOptions}
                onChange={(e) => formik.setFieldValue('English_Level', e?.label)}
                onBlur={() => formik.setFieldTouched('English_Level', true)}
                styles={{
                  control: (baseStyles: any) => ({
                    ...baseStyles,
                    height: '100%',
                    width: '100%',
                    borderRadius: '8px',
                    border: 'none',
                  }),
                }}
              />
              {formik.touched.English_Level && formik.errors.English_Level ? (
                <div className="text-danger">{formik.errors.English_Level}</div>
              ) : null}
            </div>
            <div>
              <label htmlFor="English_Level" className={`form-label ${styles.formLabel}`}>
                What is most important to you when choosing the university for you? Rank in terms of
                prefence (drag and drop to rearrange the order)
              </label>
              <div className={styles.questionContent}>
                <Reorder.Group
                  axis="y"
                  onReorder={(e) => setPriorities(e)}
                  values={priorities}
                  className={styles.optionsGroup}
                >
                  {priorities.map((item, index) => (
                    <Item key={item} item={[item, index + 1]} />
                  ))}
                </Reorder.Group>
              </div>
            </div>
            <div>
              <label htmlFor="Institution_Interest" className={`form-label ${styles.formLabel}`}>
                {"Have you already identified any universities or programs you'd like to apply to?"}
              </label>
              <input
                name="Institution_Interest"
                type="text"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.Institution_Interest}
                className={`form-control ${styles.formInput}`}
              />
              {formik.touched.Institution_Interest && formik.errors.Institution_Interest ? (
                <div className="text-danger">{formik.errors.Institution_Interest}</div>
              ) : null}
            </div>
            <div>
              <label htmlFor="Comments" className={`form-label ${styles.formLabel}`}>
                Anything else you want us to know to best prepare for your free consultation?
              </label>
              <input
                name="Comments"
                type="text"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.Comments}
                className={`form-control ${styles.formInput}`}
              />
              {formik.touched.Comments && formik.errors.Comments ? (
                <div className="text-danger">{formik.errors.Comments}</div>
              ) : null}
            </div>
            <div className="form-check">
              <input
                className="form-check-input"
                type="checkbox"
                value=""
                id="privacyPolicy"
                onChange={(e) => formik.setFieldValue('Term_Policy', !formik.values.Term_Policy)}
                onBlur={() => formik.setFieldTouched('Term_Policy', true)}
              />
              <label className={`form-check-label ${styles.formLabel}`} htmlFor="privacyPolicy">
                I agree to the{' '}
                <Link to={'https://blog.unifyapply.com/privacy-policy/'} target="_blank">
                  Privacy Policy
                </Link>
              </label>
              {formik.touched.Term_Policy && formik.errors.Term_Policy ? (
                <div className="text-danger">{formik.errors.Term_Policy}</div>
              ) : null}
            </div>
            <button
              className={`btn btn-primary ${styles.submitButton}`}
              type="submit"
              disabled={isLoading}
            >
              Request Meeting
            </button>
            <div className={styles.privacyNotes}>
              <span>
                Note: It is our responsibility to protect your privacy and we guarantee that your
                data will be completely confidential.
              </span>
            </div>
          </form>
        </div>
        <div className="col-12 col-md-6 order-md-2 order-1">
          <img
            className="img-fluid"
            src="https://storage.googleapis.com/unify_apply_assets/request-meeting.jpg"
          />
        </div>
      </div>
    </div>
  );
};

export default RequestMeeting;
